import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BrutalistCard, BrutalistInput, BrutalistButton } from './BrutalistUI';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const EditModal = ({ term, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (term) {
      setFormData({
        ...term,
        keyTerms: Array.isArray(term.keyTerms) ? term.keyTerms.join(', ') : term.keyTerms
      });
    }
  }, [term]);

  if (!isOpen || !formData) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const processedData = {
        ...formData,
        keyTerms: formData.keyTerms.split(',').map(t => t.trim()).filter(t => t !== '')
      };
      await onSave(processedData);
      onClose();
    } catch (error) {
      alert('Failed to save changes: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex justify-center p-4 sm:p-6 overflow-y-auto bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }} 
          animate={{ scale: 1, opacity: 1 }} 
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-full max-w-2xl my-auto" 
          onClick={(e) => e.stopPropagation()}
        >
          <BrutalistCard color="white" className="!p-0 overflow-hidden shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
            <div className="bg-black text-white p-6 border-b-4 border-black flex justify-between items-center">
              <h2 className="text-2xl font-black uppercase italic tracking-tighter">Edit Term</h2>
              <button onClick={onClose} className="hover:rotate-90 transition-transform">
                <SafeIcon icon={FiIcons.FiX} className="text-2xl" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 bg-[#F4F0EB]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-black uppercase">Term Name</label>
                  <BrutalistInput name="term" value={formData.term} onChange={handleChange} required />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-black uppercase">Category</label>
                  <BrutalistInput name="category" value={formData.category} onChange={handleChange} required />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-black uppercase">Plain English Definition</label>
                <textarea 
                  name="plainEnglish"
                  value={formData.plainEnglish}
                  onChange={handleChange}
                  className="w-full border-2 border-black p-3 font-bold shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] min-h-[100px]"
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-black uppercase">Simple Way to Say It</label>
                <BrutalistInput name="simpleWay" value={formData.simpleWay} onChange={handleChange} />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-black uppercase">Example Sentence</label>
                <BrutalistInput name="exampleSentence" value={formData.exampleSentence} onChange={handleChange} />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-black uppercase">Key Terms (comma separated)</label>
                <BrutalistInput name="keyTerms" value={formData.keyTerms} onChange={handleChange} placeholder="API, REST, Frontend..." />
              </div>

              <div className="pt-4 flex gap-4">
                <BrutalistButton 
                  type="submit" 
                  disabled={isSaving}
                  className="flex-grow !bg-green-400 !text-black"
                >
                  {isSaving ? 'SAVING...' : 'SAVE CHANGES'}
                </BrutalistButton>
                <BrutalistButton 
                  type="button" 
                  onClick={onClose}
                  className="!bg-white"
                >
                  CANCEL
                </BrutalistButton>
              </div>
            </form>
          </BrutalistCard>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EditModal;